import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getFacilityMap, getPickHeatmap } from "@/lib/warehouse-map.functions";
import { useScope } from "@/hooks/use-scope";
import { WarehouseCanvas } from "@/components/warehouse-map/canvas";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/app/warehouse-map/heatmap")({
  component: HeatmapTab,
});

const METRICS = [
  { id: "picks", label: "Pick frequency" },
  { id: "velocity", label: "Units picked" },
  { id: "replens", label: "Replenishments" },
  { id: "dead", label: "Dead zones" },
] as const;

function HeatmapTab() {
  const { tenantId, facilityId } = useScope();
  const getMap = useServerFn(getFacilityMap);
  const getHeat = useServerFn(getPickHeatmap);
  const [metric, setMetric] = useState<(typeof METRICS)[number]["id"]>("picks");
  const [days, setDays] = useState(30);

  const { data: map } = useQuery({
    queryKey: ["wm-map", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => getMap({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });
  const { data: heat } = useQuery({
    queryKey: ["wm-heat", tenantId, facilityId, metric, days],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => getHeat({ data: { tenantId: tenantId!, facilityId: facilityId!, metric, days } }),
  });

  const heatMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of heat?.counts ?? []) m.set(c.location_id, c.count);
    return m;
  }, [heat]);

  return (
    <div className="p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {METRICS.map((m) => (
            <Button key={m.id} size="sm" variant={metric === m.id ? "default" : "outline"} onClick={() => setMetric(m.id)}>
              {m.label}
            </Button>
          ))}
        </div>
        <div className="ml-auto flex gap-1">
          {[7, 30, 90].map((d) => (
            <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => setDays(d)}>
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {map ? (
        <WarehouseCanvas
          cols={map.map.grid_cols}
          rows={map.map.grid_rows}
          zones={map.zones}
          locations={map.locations}
          heat={heatMap}
        />
      ) : (
        <div className="h-[500px] grid place-items-center text-sm text-muted-foreground border border-dashed border-border rounded-md">
          Loading map…
        </div>
      )}

      <Card>
        <CardContent className="p-3 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <Stat label="Locations with activity" value={heatMap.size} />
          <Stat label="Peak count" value={Math.max(0, ...Array.from(heatMap.values()))} />
          <Stat label="Total events" value={Array.from(heatMap.values()).reduce((a, b) => a + b, 0)} />
          <Stat label="Window" value={`${days} days`} />
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}